import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Subject } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { QuickLinksSearchModalComponent } from '../components/quick-links/quick-links-search-modal/quick-links-search-modal.component';

export interface QuickLink {
  key: string;
  description: string;
  route?: string;
  action?: () => void;
  icon?: string;
}

@Injectable({
  providedIn: 'root'
})
export class QuickLinksService {
  private quickLinks: QuickLink[] = [];
  private quickLinksSubject = new BehaviorSubject<QuickLink[]>([]);
  private showQuickLinksSubject = new BehaviorSubject<boolean>(false);

  quickLinks$ = this.quickLinksSubject.asObservable();
  showQuickLinks$ = this.showQuickLinksSubject.asObservable();

  constructor(
    private router: Router,
    private dialog: MatDialog
  ) {
    // Initialize with some default quick links
    this.addQuickLink({
      key: 'F1',
      description: 'Search',
      icon: 'search',
      action: () => this.openSearchModal()
    });

    this.addQuickLink({
      key: 'F2',
      description: 'Dashboard',
      route: '/dashboard',
      icon: 'dashboard'
    });

    this.addQuickLink({
      key: 'F3',
      description: 'Employees',
      route: '/employees',
      icon: 'people'
    });

    this.addQuickLink({
      key: 'F4',
      description: 'Attendance',
      route: '/attendance',
      icon: 'event'
    });

    this.addQuickLink({
      key: 'F5',
      description: 'Payroll',
      route: '/payroll',
      icon: 'payments'
    });

    this.addQuickLink({
      key: 'F6',
      description: 'Properties',
      route: '/properties',
      icon: 'home'
    });

    this.addQuickLink({
      key: 'F7',
      description: 'Tasks',
      route: '/tasks',
      icon: 'task'
    });

    this.addQuickLink({
      key: 'F8',
      description: 'Reports',
      route: '/reports',
      icon: 'assessment'
    });

    this.addQuickLink({
      key: 'F9',
      description: 'Settings',
      route: '/setting',
      icon: 'settings'
    });

    this.addQuickLink({
      key: 'F10',
      description: 'My Info',
      route: '/my-info',
      icon: 'person'
    });

    this.addQuickLink({
      key: '?',
      description: 'Show/Hide Quick Links',
      icon: 'keyboard',
      action: () => this.toggleQuickLinks()
    });

    // Initialize the quick links
    this.quickLinksSubject.next(this.quickLinks);

    // Set up keyboard event listener
    this.setupKeyboardListener();
  }

  private setupKeyboardListener(): void {
    document.addEventListener('keydown', (event: KeyboardEvent) => {
      // Check if the key is a function key (F1-F12) or the question mark key
      if (event.key.startsWith('F') || event.key === '?') {
        const quickLink = this.quickLinks.find(link => link.key === event.key);
        if (quickLink) {
          event.preventDefault();
          this.activateQuickLink(quickLink);
        }
      }
    });
  }

  addQuickLink(quickLink: QuickLink): void {
    // Check if a quick link with the same key already exists
    const existingIndex = this.quickLinks.findIndex(link => link.key === quickLink.key);

    if (existingIndex >= 0) {
      // Update existing quick link
      this.quickLinks[existingIndex] = quickLink;
    } else {
      // Add new quick link
      this.quickLinks.push(quickLink);
    }

    // Update the subject
    this.quickLinksSubject.next(this.quickLinks);
  }

  removeQuickLink(key: string): void {
    this.quickLinks = this.quickLinks.filter(link => link.key !== key);
    this.quickLinksSubject.next(this.quickLinks);
  }

  activateQuickLink(quickLink: QuickLink): void {
    if (quickLink.action) {
      quickLink.action();
    } else if (quickLink.route) {
      this.router.navigate([quickLink.route]);
    }
  }

  toggleQuickLinks(): void {
    const currentValue = this.showQuickLinksSubject.value;
    this.showQuickLinksSubject.next(!currentValue);
  }

  openSearchModal(): void {
    this.dialog.open(QuickLinksSearchModalComponent, {
      width: '600px',
      data: { quickLinks: this.quickLinks }
    });
  }
}
